import { fetchEmployeeRefreshToken } from "@/shared/api";
import { IGNORE_ERROR_CODE } from "@/shared/error";
import { getCookie, resetAuth, setAuth, startActivityMonitor, stopActivityMonitor, useGlobalErrorHandler } from "@/shared/lib";
import { GlobalErrorBoundary, Modal, ScrollToTop, Toast } from "@/shared/ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Outlet, createRootRoute, redirect } from "@tanstack/react-router";
import { ReactNode, useEffect } from "react";
import { ErrorBoundary, useErrorBoundary } from "react-error-boundary";

// React Query 클라이언트 생성
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			throwOnError(error) {
				console.error("[throwOnError] ", error.name, error.message);
				return !IGNORE_ERROR_CODE.includes(error.message);
			}
		},
		mutations: {
			onError(error, variables, context) {
				console.error("[mutations - onError]", error, variables, context);

				if (!IGNORE_ERROR_CODE.includes(error.message)) {
					Toast.error(error.message || "");
				}
			},
			throwOnError(error) {
				// 모든 에러메시지 토스트 처리
				console.error("throwOnError - onError", error.message || "");
				// Toast.error(error.message || "");
				return false;
			}
		}
	}
});

const LOGIN_AUTH_PATHS = ["/login"];
const NON_AUTH_PATHS = [...LOGIN_AUTH_PATHS, "/deeplink", "/deeplink/sales-management-change"];

export const Route = createRootRoute({
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	beforeLoad: async (ctx): Promise<any> => {
		try {
			const pathName = ctx.location.pathname;
			// biome-ignore lint/suspicious/noExplicitAny: <explanation>
			const search = ctx.location.search as any;

			const accessToken = getCookie("ACCESS_TOKEN");
			const refreshToken = getCookie("REFRESH_TOKEN");
			const tempToken = getCookie("TEMP_TOKEN");

			// if (LOGIN_AUTH_PATHS.includes(pathName) && search.step !== "RESET_PASSWORD") {
			// 	removeCookie("ACCESS_TOKEN");
			// 	removeCookie("REFRESH_TOKEN");
			// }

			if (LOGIN_AUTH_PATHS.includes(pathName) && accessToken && refreshToken) {
				console.log("[REDIRECT] 로그인 페이지 접근 시도, 메인으로 리다이렉트");
				return redirect({ to: "/" });
			}

			if (LOGIN_AUTH_PATHS.includes(pathName) && search.step === "PHONE_AUTH" && !tempToken) {
				return redirect({ to: "/login", search: { step: "OPENING" } });
			}

			if (
				(!NON_AUTH_PATHS.includes(pathName) ||
					(LOGIN_AUTH_PATHS.includes(pathName) && search.step === "RESET_PASSWORD")) &&
				!accessToken &&
				!refreshToken
			) {
				return redirect({ to: "/login", search: { step: "OPENING" } });
			}

			if (refreshToken) {
				const { accessToken: newAccessToken, refreshToken: newRefreshToken } = await fetchEmployeeRefreshToken({
					refreshToken
				});
				return setAuth({ accessToken: newAccessToken, refreshToken: newRefreshToken });
			}
		} catch {
			resetAuth();
			return redirect({ to: "/login", search: { step: "OPENING" } });
		}
	},

	component: () => (
		<ErrorBoundary FallbackComponent={GlobalErrorBoundary} onReset={() => queryClient.resetQueries()}>
			<GlobalFunction>
				<QueryClientProvider client={queryClient}>
					<Outlet />
					{/* <Suspense>
						<TanStackRouterDevtools />
					</Suspense> */}
					<Toast />
					<Modal />
					<ScrollToTop />
				</QueryClientProvider>
			</GlobalFunction>
		</ErrorBoundary>
	)
});

function GlobalFunction({ children }: { children: ReactNode | ReactNode[] }) {
	const { showBoundary } = useErrorBoundary();

	// 활동 모니터링 (30분 미사용 시 자동 로그아웃)
	useEffect(() => {
		const accessToken = getCookie("ACCESS_TOKEN");
		if (accessToken) {
			startActivityMonitor();
		}
		return () => stopActivityMonitor();
	}, []);

	const onErrorCallback = (error: Error) => {
		showBoundary(error);
	};

	const onPromiseRejectionCallback = (error: Error) => {
		showBoundary(error);
	};

	useGlobalErrorHandler({ onErrorCallback, onPromiseRejectionCallback });

	return children;
}

// const TanStackRouterDevtools =
// 	process.env.NODE_ENV === "production"
// 		? () => null
// 		: lazy(() =>
// 				import("@tanstack/router-devtools").then((res) => ({
// 					default: res.TanStackRouterDevtools
// 				}))
// 			);
